import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { apiFetch, toCurrency, downloadCsv } from '../utils';

function formatDate(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  return `${d}/${m}/${y}`;
}

function SortIcon({ active, direction }) {
  if (!active) return null;
  return (
    <svg
      className={`ml-1 inline h-3 w-3 text-slate-500 transition-transform ${direction === 'desc' ? 'rotate-180' : ''}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
    </svg>
  );
}

function taxYearStart(dateStr) {
  const d = new Date(dateStr);
  const year = d.getFullYear();
  const month = d.getMonth() + 1;
  const day = d.getDate();
  if (month > 4 || (month === 4 && day >= 6)) return year;
  return year - 1;
}

function taxYearLabel(startYear) {
  return `${startYear}-${startYear + 1}`;
}

const HIDDEN_KEYS = new Set(['locked_attributes', 'locked_reason', 'attachment']);

function prettyKey(key) {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function DetailPanel({ row, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (row.type === 'Expense') {
      setDetail(row.source);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const params = new URLSearchParams();
    if (row.source?.transaction_url) {
      params.set('url', row.source.transaction_url);
    }
    params.set('dated_on', row.dated_on);
    params.set('paid_user', row.source?.paid_user || '');
    params.set('amount', String(row.source?.raw_amount || ''));

    apiFetch(`/getBankTransaction?${params.toString()}`)
      .then((data) => {
        if (data?.error) throw new Error(data.error);
        setDetail(data);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [row]);

  const entries = detail
    ? Object.entries(detail).filter(([k]) => !HIDDEN_KEYS.has(k))
    : [];

  return (
    <div className="border-t border-slate-200 bg-slate-50">
      <div className="flex items-center justify-between px-6 py-3">
        <h4 className="text-sm font-bold text-slate-900">
          {row.type === 'Expense' ? 'Expense Detail' : 'Bank Transaction Detail'}
        </h4>
        <button
          onClick={onClose}
          className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-700"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="max-h-64 overflow-y-auto px-6 pb-4">
        {loading ? (
          <div className="flex items-center gap-2 py-4">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-slate-200 border-t-slate-900" />
            <span className="text-sm text-slate-600">Loading details...</span>
          </div>
        ) : error ? (
          <p className="py-4 text-sm text-red-600">{error}</p>
        ) : (
          <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-1 text-sm">
            {entries.map(([key, value]) => (
              <div key={key} className="contents">
                <dt className="whitespace-nowrap py-1 font-medium text-slate-500">{prettyKey(key)}</dt>
                <dd className="break-all py-1 text-slate-900">
                  {typeof value === 'boolean'
                    ? value ? 'Yes' : 'No'
                    : key.includes('date') || key === 'dated_on' || key === 'updated_at' || key === 'created_at'
                      ? formatDate(String(value).split('T')[0])
                      : String(value ?? '')}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    </div>
  );
}

export default function UserExpensesModal({ user, onClose }) {
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedYear, setSelectedYear] = useState('');
  const [sortBy, setSortBy] = useState('dated_on');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedRow, setSelectedRow] = useState(null);
  const [checkedSet, setCheckedSet] = useState(new Set());

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [expensesRes, paymentsRes] = await Promise.all([
          apiFetch('/getExpensesData'),
          apiFetch('/getExpensePayments'),
        ]);

        const combined = [];

        const expensesList = Array.isArray(expensesRes) ? expensesRes : Array.isArray(expensesRes?.expenses) ? expensesRes.expenses : [];
        expensesList
          .filter((e) => e.user === user.url)
          .forEach((e) => {
            combined.push({
              dated_on: e.dated_on,
              description: e.description || '',
              type: 'Expense',
              amount: Math.abs(parseFloat(e.gross_value) || 0),
              taxYear: taxYearStart(e.dated_on),
              source: e,
            });
          });

        const paymentsList = Array.isArray(paymentsRes) ? paymentsRes : [];
        paymentsList
          .filter((p) => p.paid_user === user.url)
          .forEach((p) => {
            combined.push({
              dated_on: p.dated_on,
              description: `Payment to ${p.user}`,
              type: 'Payment',
              amount: parseFloat(p.amount) || 0,
              taxYear: taxYearStart(p.dated_on),
              source: {
                paid_user: p.paid_user,
                transaction_url: p.transaction_url || null,
                raw_amount: p.amount,
              },
            });
          });

        if (!cancelled) {
          setAllRows(combined);
          if (combined.length > 0) {
            const latest = Math.max(...combined.map((r) => r.taxYear));
            setSelectedYear(String(latest));
          }
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [user]);

  const yearOptions = useMemo(() => {
    const years = [...new Set(allRows.map((r) => r.taxYear))].sort((a, b) => b - a);
    return [
      { value: '', label: 'All Years' },
      ...years.map((y) => ({ value: String(y), label: taxYearLabel(y) })),
    ];
  }, [allRows]);

  const yearFiltered = selectedYear
    ? allRows.filter((r) => r.taxYear === Number(selectedYear))
    : allRows;

  useEffect(() => {
    setCheckedSet(new Set());
  }, [selectedYear, search, typeFilter]);

  const handleSort = (col) => {
    if (sortBy === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setSortDir(col === 'dated_on' ? 'desc' : 'asc');
    }
  };

  const filtered = yearFiltered.filter((r) => {
    if (typeFilter !== 'all' && r.type !== typeFilter) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      return (
        r.description.toLowerCase().includes(q) ||
        r.dated_on.includes(q) ||
        r.type.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    let va, vb;
    if (sortBy === 'expense' || sortBy === 'payment') {
      va = a.type === (sortBy === 'expense' ? 'Expense' : 'Payment') ? a.amount : 0;
      vb = b.type === (sortBy === 'expense' ? 'Expense' : 'Payment') ? b.amount : 0;
    } else {
      va = (a[sortBy] || '').toLowerCase();
      vb = (b[sortBy] || '').toLowerCase();
    }
    if (va < vb) return -1 * dir;
    if (va > vb) return 1 * dir;
    return 0;
  });

  const totalExpenses = sorted.filter((r) => r.type === 'Expense').reduce((s, r) => s + r.amount, 0);
  const totalPayments = sorted.filter((r) => r.type === 'Payment').reduce((s, r) => s + r.amount, 0);
  const delta = totalExpenses - totalPayments;
  const deltaNegative = delta > 0;

  const allChecked = sorted.length > 0 && checkedSet.size === sorted.length;
  const someChecked = checkedSet.size > 0 && checkedSet.size < sorted.length;

  const toggleAll = useCallback(() => {
    if (allChecked) {
      setCheckedSet(new Set());
    } else {
      setCheckedSet(new Set(sorted.map((_, i) => i)));
    }
  }, [allChecked, sorted]);

  const toggleOne = useCallback((idx) => {
    setCheckedSet((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  }, []);

  const handleRowClick = useCallback((row) => {
    setSelectedRow((prev) => (prev === row ? null : row));
  }, []);

  // Drag-to-select
  const dragStartIdx = useRef(null);
  const isDragging = useRef(false);
  const [dragRange, setDragRange] = useState(null); // { from, to }
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  const handleMouseDown = useCallback((e, idx) => {
    if (e.button !== 0) return;
    dragStartIdx.current = idx;
    isDragging.current = false;
    setDragRange(null);
  }, []);

  const handleMouseEnter = useCallback((e, idx) => {
    if (dragStartIdx.current === null) return;
    const start = dragStartIdx.current;
    if (start !== idx) isDragging.current = true;
    if (isDragging.current) {
      const from = Math.min(start, idx);
      const to = Math.max(start, idx);
      setDragRange({ from, to });
      setMousePos({ x: e.clientX, y: e.clientY });
    }
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (isDragging.current && dragRange) {
      setMousePos({ x: e.clientX, y: e.clientY });
    }
  }, [dragRange]);

  useEffect(() => {
    const handleUp = () => {
      if (isDragging.current) {
        // keep dragRange visible until next click
      } else {
        setDragRange(null);
      }
      dragStartIdx.current = null;
      isDragging.current = false;
    };
    window.addEventListener('mouseup', handleUp);
    return () => window.removeEventListener('mouseup', handleUp);
  }, []);

  const dragTotals = useMemo(() => {
    if (!dragRange) return null;
    const selected = sorted.slice(dragRange.from, dragRange.to + 1);
    const expenses = selected.filter((r) => r.type === 'Expense').reduce((s, r) => s + r.amount, 0);
    const payments = selected.filter((r) => r.type === 'Payment').reduce((s, r) => s + r.amount, 0);
    return { count: selected.length, expenses, payments };
  }, [dragRange, sorted]);

  const isInDragRange = useCallback((idx) => {
    if (!dragRange) return false;
    return idx >= dragRange.from && idx <= dragRange.to;
  }, [dragRange]);

  const clearDragSelection = useCallback(() => {
    setDragRange(null);
    dragStartIdx.current = null;
    isDragging.current = false;
  }, []);

  const handleExportCsv = useCallback(() => {
    const rows = checkedSet.size > 0
      ? sorted.filter((_, i) => checkedSet.has(i))
      : sorted;
    const expTotal = rows.filter((r) => r.type === 'Expense').reduce((s, r) => s + r.amount, 0);
    const payTotal = rows.filter((r) => r.type === 'Payment').reduce((s, r) => s + r.amount, 0);
    const d = expTotal - payTotal;
    const yearLabel = selectedYear ? taxYearLabel(Number(selectedYear)) : 'All';
    const lines = ['Date,Description,Type,Amount'];
    rows.forEach((r) => {
      lines.push(
        `${formatDate(r.dated_on)},"${r.description.replace(/"/g, '""')}",${r.type},${r.amount.toFixed(2)}`
      );
    });
    lines.push('');
    lines.push(`Total Expenses,,Expense,${expTotal.toFixed(2)}`);
    lines.push(`Total Payments,,Payment,${payTotal.toFixed(2)}`);
    lines.push(`Delta,,,${d.toFixed(2)}`);
    const name = `${user.first_name}_${user.last_name}`.replace(/\s+/g, '_');
    downloadCsv(`expenses_${name}_${yearLabel}.csv`, lines.join('\n'));
  }, [sorted, checkedSet, selectedYear, user]);

  if (!user) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 pt-16" onClick={onClose}>
      <div
        className="w-full max-w-4xl rounded-xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4">
          <h3 className="text-lg font-bold text-slate-900">
            Expenses &mdash; {user.first_name} {user.last_name}
          </h3>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3 border-t border-slate-100 px-6 py-3" onClick={clearDragSelection}>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            className="h-9 rounded-lg bg-slate-50 px-3 text-sm font-medium text-slate-900 shadow-sm outline-none focus:ring-2 focus:ring-slate-300"
          >
            {yearOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search description, date..."
            className="h-9 w-52 rounded-lg bg-slate-50 px-3 text-sm text-slate-900 placeholder-slate-400 shadow-sm outline-none focus:ring-2 focus:ring-slate-300"
          />
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="h-9 rounded-lg bg-slate-50 px-3 text-sm text-slate-900 shadow-sm outline-none focus:ring-2 focus:ring-slate-300"
          >
            <option value="all">All Types</option>
            <option value="Expense">Expenses</option>
            <option value="Payment">Payments</option>
          </select>
          <span className="ml-auto text-xs text-slate-500">
            {sorted.length} row{sorted.length !== 1 ? 's' : ''}
          </span>
          {sorted.length > 0 && (
            <button
              onClick={handleExportCsv}
              className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-emerald-600 px-4 text-sm font-medium text-white shadow-sm transition-colors hover:bg-emerald-700"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              {checkedSet.size > 0 ? `Export ${checkedSet.size} Selected` : 'Export All'}
            </button>
          )}
        </div>

        {/* Content */}
        <div className="max-h-[60vh] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center gap-3 py-12">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-slate-200 border-t-slate-900" />
              <span className="text-sm text-slate-600">Loading expenses...</span>
            </div>
          ) : error ? (
            <div className="px-6 py-8 text-center text-sm text-red-600">{error}</div>
          ) : sorted.length === 0 ? (
            <div className="px-6 py-8 text-center text-sm text-slate-500">
              {allRows.length === 0 ? 'No expenses or payments found for this user.' : 'No rows match current filters.'}
            </div>
          ) : (
            <table className="min-w-full text-left text-sm">
              <thead className="sticky top-0 bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="w-10 px-3 py-3 text-center">
                    <input
                      type="checkbox"
                      checked={allChecked}
                      ref={(el) => { if (el) el.indeterminate = someChecked; }}
                      onChange={toggleAll}
                      className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                    />
                  </th>
                  <th className="cursor-pointer select-none px-5 py-3 transition-colors hover:bg-slate-100" onClick={() => handleSort('dated_on')}>
                    Date <SortIcon active={sortBy === 'dated_on'} direction={sortDir} />
                  </th>
                  <th className="cursor-pointer select-none px-5 py-3 transition-colors hover:bg-slate-100" onClick={() => handleSort('description')}>
                    Description <SortIcon active={sortBy === 'description'} direction={sortDir} />
                  </th>
                  <th className="cursor-pointer select-none px-5 py-3 text-right transition-colors hover:bg-slate-100" onClick={() => handleSort('expense')}>
                    Expense <SortIcon active={sortBy === 'expense'} direction={sortDir} />
                  </th>
                  <th className="cursor-pointer select-none px-5 py-3 text-right transition-colors hover:bg-slate-100" onClick={() => handleSort('payment')}>
                    Payment <SortIcon active={sortBy === 'payment'} direction={sortDir} />
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 select-none">
                {sorted.map((row, i) => (
                  <tr
                    key={i}
                    className={`cursor-pointer transition-colors ${
                      isInDragRange(i)
                        ? 'bg-indigo-100'
                        : selectedRow === row
                          ? 'bg-indigo-50'
                          : 'hover:bg-slate-50'
                    }`}
                    onMouseDown={(e) => handleMouseDown(e, i)}
                    onMouseEnter={(e) => handleMouseEnter(e, i)}
                    onMouseMove={handleMouseMove}
                    onClick={() => {
                      if (!isDragging.current) handleRowClick(row);
                    }}
                  >
                    <td className="w-10 px-3 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={checkedSet.has(i)}
                        onChange={() => toggleOne(i)}
                        className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                      />
                    </td>
                    <td className="whitespace-nowrap px-5 py-3 text-slate-900">{formatDate(row.dated_on)}</td>
                    <td className="px-5 py-3 text-slate-700">{row.description}</td>
                    <td className="whitespace-nowrap px-5 py-3 text-right text-blue-700">
                      {row.type === 'Expense' ? toCurrency(row.amount) : ''}
                    </td>
                    <td className="whitespace-nowrap px-5 py-3 text-right text-emerald-700">
                      {row.type === 'Payment' ? toCurrency(row.amount) : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-50 text-sm font-semibold">
                <tr>
                  <td className="px-5 py-3 text-slate-900" colSpan={3}>Total</td>
                  <td className="px-5 py-3 text-right text-blue-700">{toCurrency(totalExpenses)}</td>
                  <td className="px-5 py-3 text-right text-emerald-700">{toCurrency(totalPayments)}</td>
                </tr>
                <tr className="border-t border-slate-200">
                  <td className="px-5 py-3 text-slate-900" colSpan={3}>Delta (Expenses &minus; Payments)</td>
                  <td className={`px-5 py-3 text-right ${deltaNegative ? 'text-red-600' : 'text-slate-900'}`} colSpan={2}>
                    {deltaNegative ? '−' : ''}{toCurrency(Math.abs(delta))}
                  </td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>

        {/* Drag selection totals popup */}
        {dragTotals && (
          <div
            className="fixed z-[60] rounded-lg bg-slate-900 px-4 py-3 text-xs text-white shadow-xl"
            style={{ left: mousePos.x + 16, top: mousePos.y - 20, pointerEvents: 'none' }}
          >
            <div className="mb-1 font-semibold">{dragTotals.count} row{dragTotals.count !== 1 ? 's' : ''} selected</div>
            {dragTotals.expenses > 0 && (
              <div className="flex justify-between gap-4">
                <span className="text-blue-300">Expenses:</span>
                <span className="font-medium">{toCurrency(dragTotals.expenses)}</span>
              </div>
            )}
            {dragTotals.payments > 0 && (
              <div className="flex justify-between gap-4">
                <span className="text-emerald-300">Payments:</span>
                <span className="font-medium">{toCurrency(dragTotals.payments)}</span>
              </div>
            )}
            {dragTotals.expenses > 0 && dragTotals.payments > 0 && (
              <div className="mt-1 flex justify-between gap-4 border-t border-slate-700 pt-1">
                <span className="text-slate-300">Delta:</span>
                <span className={`font-medium ${dragTotals.expenses > dragTotals.payments ? 'text-red-400' : 'text-slate-100'}`}>
                  {dragTotals.expenses > dragTotals.payments ? '−' : ''}{toCurrency(Math.abs(dragTotals.expenses - dragTotals.payments))}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Detail Panel */}
        {selectedRow && (
          <DetailPanel row={selectedRow} onClose={() => setSelectedRow(null)} />
        )}

        {/* Footer */}
        <div className="flex justify-end border-t border-slate-100 px-6 py-3">
          <button
            onClick={onClose}
            className="h-9 rounded-lg bg-slate-900 px-4 text-sm font-medium text-white shadow-sm transition-colors hover:bg-slate-800"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
